// dependency-graph-ignore-file

import {
  analyticsClient,
  getDatabaseContainerType,
  SendDatabaseDeleteEntryEventSources,
} from '@databases/analytics';
import {
  InlineRovoPromptTriggerSource,
  openInlineRovoPrompt,
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { type PanelManager } from '@databases/panel-types';
import {
  doClearValuesWithIds,
  doDeleteEntriesWithIds,
} from '@databases/redux/contextual-menu/actions';
import { appStore } from '@databases/redux/store';
import { type SelectionsApi } from '@databases/selections-api';

import { type CellActionsAPI } from '../types';
import {
  getSelectedEntryIdsFromCells,
  getSelectedFieldIdsFromCells,
} from '../utils/resolve-cell-selection';

/**
 * Builds the action handlers for cell-level operations in the action bar.
 */
export const buildCellActions = ({
  contentId,
  selectionsApi,
  panelManager,
}: {
  contentId: string;
  selectionsApi: SelectionsApi;
  panelManager: PanelManager;
}): CellActionsAPI => {
  const dispatch = appStore.dispatch;

  /**
   * Clears the values in all selected cells.
   */
  const handleClearFields = () => {
    const resolved = selectionsApi.getResolvedSelection();
    const entryIds = getSelectedEntryIdsFromCells(resolved);
    const fieldIds = getSelectedFieldIdsFromCells(resolved);

    if (entryIds.length === 0 || fieldIds.length === 0) {
      return;
    }

    dispatch(doClearValuesWithIds(contentId, entryIds, fieldIds));
  };

  /**
   * Deletes all entries that contain selected cells and clears the selection.
   */
  const handleDeleteEntries = () => {
    const resolved = selectionsApi.getResolvedSelection();
    const entryIds = getSelectedEntryIdsFromCells(resolved);

    if (entryIds.length === 0) {
      return;
    }

    analyticsClient.sendDeleteEntryEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      source: SendDatabaseDeleteEntryEventSources.ACTION_BAR,
    });

    const panelData = panelManager.getData('EntryViewPanel');

    if (panelData?.entryId && entryIds.includes(panelData.entryId)) {
      panelManager.hidePanel();
    }

    dispatch(doDeleteEntriesWithIds(contentId, entryIds));

    selectionsApi.clearPreviewIntent();
    selectionsApi.deselectAll();
  };

  /**
   * Opens the inline Rovo AI prompt for the selected cells.
   */
  const handleAskRovo = () => {
    openInlineRovoPrompt(InlineRovoPromptTriggerSource.TableEntry);
  };

  return {
    handleAskRovo,
    handleClearFields,
    handleDeleteEntries,
  };
};
